import { isOtherCompositionBucket } from '@revelio/core/composition-buckets';

import type { MetricValues } from '../../PageConfigProvider/types/metric';
import {
  DataType,
  type LeveledBreakdownSingleDataSet,
  type NestedBreakdownSingleDataSet,
  type NestedDataPoint,
  type SingleDataPoint,
  type TableData,
  type TableDimension,
} from '../types';
import {
  getTableDimensionValue,
  getTableValue,
  groupTableRows,
} from './table-data';

const addValue = (
  sum: number | null | undefined,
  value: number | null | undefined
): number | null => {
  if (sum === null || sum === undefined) return value ?? null;
  return value === null || value === undefined ? sum : sum + value;
};

export const tableToBreakdownSingleDataSet = (
  table: TableData,
  metric: MetricValues | undefined = table.metrics[0]
): LeveledBreakdownSingleDataSet | NestedBreakdownSingleDataSet | undefined => {
  const [dimension, category, subcategory, ...otherDimensions] =
    table.dimensions;
  if (
    dimension === undefined ||
    category === undefined ||
    dimension === 'timeseries' ||
    category === 'timeseries' ||
    subcategory === 'timeseries' ||
    otherDimensions.length > 0 ||
    metric === undefined ||
    !table.metrics.includes(metric)
  ) {
    return undefined;
  }
  const hasShare = table.metrics.includes('share_of_headcount');

  const pointAt = (row: number, column: TableDimension): SingleDataPoint => {
    const value = getTableDimensionValue(table, column, row);
    return {
      dim: value?.label ?? '',
      value: getTableValue(table, metric, row),
      ...(hasShare
        ? { share: getTableValue(table, 'share_of_headcount', row) }
        : {}),
      drilldownId: value?.id ?? null,
    };
  };

  const nest = (
    rows: readonly number[],
    below: TableDimension
  ): NestedDataPoint[] => {
    const segments = new Map<string, NestedDataPoint>();
    const subtotals = new Map<string, SingleDataPoint>();
    for (const row of rows) {
      const segment = pointAt(row, category);
      const key = segment.drilldownId ?? segment.dim;
      const nested = segments.get(key) ?? {
        ...segment,
        value: null,
        ...(hasShare ? { share: null } : {}),
        children: [],
      };
      segments.set(key, nested);
      if (getTableDimensionValue(table, below, row) === undefined) {
        subtotals.set(key, segment);
        continue;
      }
      const cell = pointAt(row, below);
      nested.value = addValue(nested.value, cell.value);
      if (hasShare) nested.share = addValue(nested.share, cell.share);
      if (!isOtherCompositionBucket(cell.drilldownId)) {
        nested.children.push(cell);
      }
    }
    return Array.from(segments, ([key, nested]) => {
      const subtotal = subtotals.get(key);
      return subtotal
        ? {
            ...nested,
            value: subtotal.value,
            ...(hasShare ? { share: subtotal.share } : {}),
          }
        : nested;
    });
  };

  const entities = groupTableRows(table, dimension);
  if (subcategory === undefined) {
    return {
      type: DataType.BREAKDOWN_SINGLE,
      level: category,
      data: entities.map(({ value, rows }) => ({
        dim: value.label,
        breakdown: rows.map((row) => pointAt(row, category)),
      })),
    };
  }
  return {
    type: DataType.BREAKDOWN_SINGLE,
    levels: [category, subcategory],
    data: entities.map(({ value, rows }) => ({
      dim: value.label,
      breakdown: nest(rows, subcategory),
    })),
  };
};
